import React, { useState, useEffect, useRef } from 'react';
import WaveformVisualizer from '../../Common/WaveformVisualizer.jsx';
import { SoundGateBanner } from '../../Common/DesignComponents.jsx';
import useClipLookup from '../../Common/useClipLookup.js';
import formatClipTitle from '../../../utils/formatClipTitle.js';
import WinningSoundsReveal from '../../Common/WinningSoundsReveal.jsx';
import '../../../styles/theme.css';

export function FoleyHost({
  room = {},
  audioEngine = null,
  soundEnabled = false,
  onEnableSound = () => {},
  onForceAdvance = () => {}
}) {
  const lookupClip = useClipLookup();
  const modeState = room.modeState || {};
  const currentPhase = room.currentPhase || modeState.currentPhase || 'authoring';
  const duels = modeState.duels || [];
  const currentDuelIndex = modeState.currentDuelIndex || 0;
  const currentDuel = duels[currentDuelIndex];

  const [activePlayback, setActivePlayback] = useState(null); // 'A', 'B', or null
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [currentClipLabel, setCurrentClipLabel] = useState('');
  const activeHandleRef = useRef(null);

  const triggerDuelistPlayback = async (duelistSide, submission) => {
    if (!submission || !submission.clips || submission.clips.length === 0) return;
    if (!audioEngine) return;

    setActivePlayback(duelistSide);
    setIsPlaying(true);
    setPlaybackProgress(0);

    const sequenceItems = submission.clips.map((clipId, i) => {
      const fullClip = lookupClip(clipId) || { id: clipId, label: clipId };
      const gap = i === 0 ? 0 : (submission.gaps?.[i - 1] !== undefined ? submission.gaps[i - 1] : 0);
      return {
        clipId: fullClip.id || clipId,
        buffer: fullClip.buffer,
        gap
      };
    });

    try {
      const handle = await audioEngine.playSequence(sequenceItems, {
        onProgress: (data) => {
          setPlaybackProgress(data.progress || 0);
          const activeIndex = data.clipIndex || 0;
          const activeClipId = submission.clips[activeIndex];
          const activeClip = lookupClip(activeClipId);
          setCurrentClipLabel(formatClipTitle(activeClip || activeClipId || `Scene Clip ${activeIndex + 1}`));
        },
        onEnded: () => {
          setIsPlaying(false);
          setPlaybackProgress(1.0);
          activeHandleRef.current = null;

          if (duelistSide === 'A' && currentDuel) {
            const submissions = modeState.submissions || {};
            const subB = submissions[currentDuel.duelistBId];
            if (subB) {
              setTimeout(() => triggerDuelistPlayback('B', subB), 600);
            }
          }
        }
      });

      activeHandleRef.current = handle;
    } catch {
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    if (currentPhase === 'duel' && currentDuel) {
      const submissions = modeState.submissions || {};
      const subA = submissions[currentDuel.duelistAId];
      if (subA) {
        triggerDuelistPlayback('A', subA);
      }
    }
    return () => {
      if (activeHandleRef.current) {
        activeHandleRef.current.stop();
      }
    };
  }, [currentPhase, currentDuelIndex]);

  // 1. Authoring Phase Host TV View
  if (currentPhase === 'authoring') {
    const authorCount = Object.keys(modeState.authorSubmissions || {}).length;
    const totalPlayers = Object.keys(room.players || {}).filter(k => !room.players[k]?.isHost).length;

    return (
      <div
        className="foley-host-authoring"
        data-testid="foley-host-authoring"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          color: 'var(--sd-ink, #EDEFF2)',
          fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
        }}
      >
        <SoundGateBanner soundEnabled={soundEnabled} onEnableSound={onEnableSound} />

        <div
          style={{
            padding: '48px 36px',
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '12px',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--sd-teal, #3ACFCF)',
              fontWeight: 600,
            }}>
              Foley Scene — Scenario Authoring
            </div>
            {onForceAdvance && (
              <button
                type="button"
                data-testid="host-force-advance-btn"
                onClick={onForceAdvance}
                style={{
                  minHeight: '44px',
                  minWidth: '44px',
                  padding: '0 16px',
                  borderRadius: '10px',
                  background: 'var(--sd-panel-2, #0E1116)',
                  border: '1px solid var(--sd-line, #1A1F25)',
                  color: 'var(--sd-dim, #6E7982)',
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                ⏩ Force Advance
              </button>
            )}
          </div>
          <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', marginBottom: '16px' }}>
            Write a Dramatic Scene Scenario on Your Device!
          </div>
          <div style={{ fontSize: '1.2rem', color: 'var(--sd-muted, #79838D)', maxWidth: '700px', margin: '0 auto 32px' }}>
            e.g., "A heist goes wrong", "An alien invasion at a diner", or "Escape from a runaway train"
          </div>

          <div style={{
            background: 'var(--sd-panel-2, #0E1116)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            margin: '0 auto',
            border: '1px solid var(--sd-line-2, #1C2229)',
          }}>
            <div style={{ fontSize: '1.1rem', color: 'var(--sd-teal, #3ACFCF)', fontWeight: 600, marginBottom: '8px' }}>
              Scenarios Submitted
            </div>
            <div style={{
              fontSize: '3rem',
              fontWeight: 800,
              color: 'var(--sd-ink, #EDEFF2)',
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            }}>
              {authorCount} / {totalPlayers}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2. Answering Phase Host TV View
  if (currentPhase === 'answering') {
    const submissionCount = Object.keys(modeState.submissions || {}).length;
    const totalPlayers = Object.keys(room.players || {}).filter(k => !room.players[k]?.isHost).length;

    return (
      <div
        className="foley-host-answering"
        data-testid="foley-host-answering"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          color: 'var(--sd-ink, #EDEFF2)',
          fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
        }}
      >
        <SoundGateBanner soundEnabled={soundEnabled} onEnableSound={onEnableSound} />

        <div
          style={{
            padding: '48px 36px',
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
          }}
        >
          <div style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '12px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-sage, #8FC7A0)',
            fontWeight: 600,
            marginBottom: '16px',
          }}>
            Foley Scene — Radio Drama Production
          </div>
          <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', marginBottom: '16px' }}>
            Players are Crafting 3-Clip Foley Sequences!
          </div>
          <div style={{ fontSize: '1.2rem', color: 'var(--sd-muted, #79838D)', maxWidth: '700px', margin: '0 auto 32px' }}>
            Combining 3 sound clips with custom gap timing to build a miniature radio drama scene.
          </div>

          <div style={{
            background: 'var(--sd-panel-2, #0E1116)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            margin: '0 auto',
            border: '1px solid var(--sd-line-2, #1C2229)',
          }}>
            <div style={{ fontSize: '1.1rem', color: 'var(--sd-sage, #8FC7A0)', fontWeight: 600, marginBottom: '8px' }}>
              Foley Scenes Complete
            </div>
            <div style={{
              fontSize: '3rem',
              fontWeight: 800,
              color: 'var(--sd-ink, #EDEFF2)',
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            }}>
              {submissionCount} / {totalPlayers}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 3. Duel Phase Host TV View
  if (currentPhase === 'duel') {
    if (!currentDuel) {
      return (
        <div style={{
          padding: '36px',
          textAlign: 'center',
          background: 'var(--sd-panel, #0D1015)',
          border: '1px solid var(--sd-line, #1A1F25)',
          borderRadius: '20px',
          color: 'var(--sd-dim, #6E7982)',
          fontSize: '1.4rem',
        }}>
          Loading Foley Scene duels...
        </div>
      );
    }

    const promptObj = currentDuel.prompt || {};
    const submissions = modeState.submissions || {};
    const subA = submissions[currentDuel.duelistAId];
    const subB = submissions[currentDuel.duelistBId];

    return (
      <div
        className="foley-host-duel"
        data-testid="foley-host-duel"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          color: 'var(--sd-ink, #EDEFF2)',
          fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
        }}
      >
        <SoundGateBanner soundEnabled={soundEnabled} onEnableSound={onEnableSound} />

        {/* Duel Matchup Header & Scenario */}
        <div
          style={{
            padding: '28px',
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '11px',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--sd-amber, #E0A458)',
              fontWeight: 600,
            }}>
              Duel Matchup {currentDuelIndex + 1} of {duels.length}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {promptObj.authorName && (
                <span style={{
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  fontSize: '11px',
                  color: 'var(--sd-dim, #6E7982)',
                }}>
                  Scenario Author: {promptObj.authorName}
                </span>
              )}
              {onForceAdvance && (
                <button
                  type="button"
                  data-testid="host-force-advance-btn"
                  onClick={onForceAdvance}
                  style={{
                    minHeight: '36px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    background: 'var(--sd-panel-2, #0E1116)',
                    border: '1px solid var(--sd-line, #1A1F25)',
                    color: 'var(--sd-dim, #6E7982)',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  ⏩ Next Duel
                </button>
              )}
            </div>
          </div>

          <div data-testid="duel-scenario-title" style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--sd-teal, #3ACFCF)' }}>
            "{promptObj.text || 'A heist goes wrong'}"
          </div>
        </div>

        {/* Waveform Visualizer & Continuous Audio Playback */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Duelist A Card */}
          <div
            style={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              background: activePlayback === 'A' ? 'rgba(58, 207, 207, 0.08)' : 'var(--sd-panel-2, #0E1116)',
              border: `1px solid ${activePlayback === 'A' ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-line-2, #1C2229)'}`,
              borderRadius: '16px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)' }}>
                🅰️ {currentDuel.duelistA?.name || 'Player A'}
              </div>
              <button
                type="button"
                onClick={() => triggerDuelistPlayback('A', subA)}
                style={{
                  background: 'var(--sd-teal, #3ACFCF)',
                  color: 'var(--sd-ground, #08090B)',
                  border: 'none',
                  padding: '8px 18px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  minHeight: '44px',
                  fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                }}
                data-testid="play-duelist-a-btn"
              >
                ▶ Play Scene A
              </button>
            </div>

            {subA ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {subA.clips && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {subA.clips.map((clip, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                          fontSize: '11px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--sd-line, #1A1F25)',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          color: 'var(--sd-ink-2, #C6CDD4)',
                        }}
                      >
                        {idx + 1}. {formatClipTitle(clip)}
                      </span>
                    ))}
                  </div>
                )}
                <div style={{
                  fontSize: '0.8rem',
                  color: 'var(--sd-dim, #6E7982)',
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                }}>
                  Gaps: [{subA.gaps ? subA.gaps.join('s, ') + 's' : '0.5s, 0.5s'}]
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--sd-rose, #E4738A)', fontSize: '0.9rem' }}>No scene submitted</div>
            )}

            {activePlayback === 'A' && (
              <WaveformVisualizer
                isPlaying={isPlaying}
                progress={playbackProgress}
                playerName={currentDuel.duelistA?.name}
                playerAvatar={currentDuel.duelistA?.avatar || '🎧'}
                clipTitle={currentClipLabel || 'Scene A Sequence'}
                color="var(--sd-teal, #3ACFCF)"
              />
            )}
          </div>

          {/* Duelist B Card */}
          <div
            style={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              background: activePlayback === 'B' ? 'rgba(224, 164, 88, 0.08)' : 'var(--sd-panel-2, #0E1116)',
              border: `1px solid ${activePlayback === 'B' ? 'var(--sd-amber, #E0A458)' : 'var(--sd-line-2, #1C2229)'}`,
              borderRadius: '16px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)' }}>
                🅱️ {currentDuel.duelistB?.name || 'Player B'}
              </div>
              <button
                type="button"
                onClick={() => triggerDuelistPlayback('B', subB)}
                style={{
                  background: 'var(--sd-amber, #E0A458)',
                  color: 'var(--sd-ground, #08090B)',
                  border: 'none',
                  padding: '8px 18px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  minHeight: '44px',
                  fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                }}
                data-testid="play-duelist-b-btn"
              >
                ▶ Play Scene B
              </button>
            </div>

            {subB ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {subB.clips && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {subB.clips.map((clip, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                          fontSize: '11px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--sd-line, #1A1F25)',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          color: 'var(--sd-ink-2, #C6CDD4)',
                        }}
                      >
                        {idx + 1}. {formatClipTitle(clip)}
                      </span>
                    ))}
                  </div>
                )}
                <div style={{
                  fontSize: '0.8rem',
                  color: 'var(--sd-dim, #6E7982)',
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                }}>
                  Gaps: [{subB.gaps ? subB.gaps.join('s, ') + 's' : '0.5s, 0.5s'}]
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--sd-rose, #E4738A)', fontSize: '0.9rem' }}>No scene submitted</div>
            )}

            {activePlayback === 'B' && (
              <WaveformVisualizer
                isPlaying={isPlaying}
                progress={playbackProgress}
                playerName={currentDuel.duelistB?.name}
                playerAvatar={currentDuel.duelistB?.avatar || '🎵'}
                clipTitle={currentClipLabel || 'Scene B Sequence'}
                color="var(--sd-amber, #E0A458)"
              />
            )}
          </div>
        </div>

        {/* Voting Instructions */}
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
          }}
        >
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--sd-amber, #E0A458)' }}>
            🗳️ Players are voting on their devices for the best Foley Scene!
          </div>
        </div>
      </div>
    );
  }

  // 4. Results Phase Host TV View
  const playersList = Object.values(room.players || {}).filter(p => !p.isHost).sort((a, b) => (b.score || 0) - (a.score || 0));

  return (
    <div
      className="foley-host-results"
      data-testid="foley-host-results"
      style={{
        width: '100%',
        color: 'var(--sd-ink, #EDEFF2)',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
      }}
    >
      <div
        style={{
          padding: '36px',
          background: 'var(--sd-panel, #0D1015)',
          border: '1px solid var(--sd-line, #1A1F25)',
          borderRadius: '20px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <span style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '11px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-sage, #8FC7A0)',
            fontWeight: 600,
          }}>
            Foley Scene Standings
          </span>
          <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', marginTop: '8px' }}>
            🏆 Leaderboard
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '600px', margin: '0 auto' }}>
          {playersList.map((player, rank) => (
            <div
              key={player.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 24px',
                background: rank === 0 ? 'rgba(224, 164, 88, 0.12)' : 'var(--sd-panel-2, #0E1116)',
                border: `1px solid ${rank === 0 ? 'var(--sd-amber, #E0A458)' : 'var(--sd-line-2, #1C2229)'}`,
                borderRadius: '16px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{
                  fontSize: '1.3rem',
                  fontWeight: 800,
                  color: rank === 0 ? 'var(--sd-amber, #E0A458)' : 'var(--sd-dim, #6E7982)',
                  width: '32px',
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                }}>
                  #{rank + 1}
                </span>
                <span style={{ fontSize: '1.4rem' }}>{player.avatar || '🎧'}</span>
                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)' }}>
                  {player.name}
                </span>
              </div>

              <div style={{
                fontSize: '1.4rem',
                fontWeight: 800,
                color: 'var(--sd-teal, #3ACFCF)',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              }}>
                {player.score || 0} pts
              </div>
            </div>
          ))}
        </div>

        <WinningSoundsReveal room={room} audioEngine={audioEngine} />
      </div>
    </div>
  );
}

export default FoleyHost;
